import React, { useEffect, useRef, useState } from 'react';
import { messagingRequest as api, dateLabel } from './api';
import { Field } from './UI';

export default function DeliverySettings({ meta, notify, onSaved }) {
  const [settings, setSettings] = useState(null),
    [busy, setBusy] = useState(false),
    [dirty, setDirty] = useState(false),
    [ack, setAck] = useState(false);
  const [secrets, setSecrets] = useState({}),
    working = useRef(false);
  useEffect(() => {
    let current = true;
    api('/settings')
      .then(data => {
        if (current) setSettings(data);
      })
      .catch(e => notify(e.message, 'error'));
    return () => {
      current = false;
    };
  }, [notify]);
  const work = async fn => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      working.current = false;
      setBusy(false);
    }
  };
  if (!settings) return <p className="msg-empty">Loading delivery settings…</p>;
  const change = patch => {
    setSettings(s => ({ ...s, ...patch }));
    setDirty(true);
    setAck(false);
  };
  const credential = (field, value) => {
    setSecrets(s => ({ ...s, [field]: value }));
    setDirty(true);
    setAck(false);
  };
  const storeChange = (store, field, value) => {
    const found = settings.stores.find(s => s.store === store);
    const row = {
      store,
      smsFrom: '',
      fromEmail: '',
      replyTo: '',
      postalAddress: '',
      timezone: 'America/Chicago',
      ...found,
      [field]: value
    };
    change({ stores: [...settings.stores.filter(s => s.store !== store), row] });
  };
  const persist = async enabled => {
    const result = await api('/settings', { ...settings, ...secrets, enabled, providerReadyAcknowledged: ack }, 'PUT');
    setSettings(result);
    setSecrets({});
    setDirty(false);
    setAck(false);
    onSaved();
    return result;
  };
  return (
    <section className="msg-settings">
      <div className="msg-section-heading">
        <div>
          <h2>Connect MHC’s delivery accounts</h2>
          <p>Your team sends and replies inside the CRM. These accounts handle delivery.</p>
        </div>
        <span className="msg-admin-label">Super Admin</span>
      </div>
      <div className="msg-setup-grid">
        <div className="msg-panel">
          <h3>Text messaging · Twilio</h3>
          <p className="msg-muted">
            Use an SMS-capable business number for each store. Complete the applicable sender registration in your
            Twilio account.
          </p>
          <Field
            label="Account SID"
            value={secrets.twilioAccount || ''}
            onChange={e => credential('twilioAccount', e.target.value)}
            placeholder={settings.credentials.twilio ? 'Credentials saved — enter only to replace' : 'AC…'}
            autoComplete="off"
          />
          <Field
            label="Auth token"
            type="password"
            value={secrets.twilioToken || ''}
            onChange={e => credential('twilioToken', e.target.value)}
            autoComplete="new-password"
          />
          <Field
            label="Inbound message webhook"
            readOnly
            value={settings.callbacks.sms}
            hint="Set each store number’s incoming SMS webhook to this URL, using HTTP POST."
          />
          <p className="msg-connection-detail">
            {settings.verified.sms ? '✓ Text connection checked' : 'Text connection not verified'}
          </p>
        </div>
        <div className="msg-panel">
          <h3>Email campaigns · Amazon SES</h3>
          <p className="msg-muted">
            Use SES production access and verified sender identities. Connect an SNS notification topic to your
            configuration set for Delivery, Bounce, and Complaint events.
          </p>
          <div className="msg-form-pair">
            <Field
              label="AWS access key ID"
              type="password"
              value={secrets.awsKey || ''}
              onChange={e => credential('awsKey', e.target.value)}
              autoComplete="new-password"
              placeholder={settings.credentials.email ? 'Saved — leave blank to keep' : ''}
            />
            <Field
              label="AWS secret access key"
              type="password"
              value={secrets.awsSecret || ''}
              onChange={e => credential('awsSecret', e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div className="msg-form-pair">
            <Field
              label="AWS region"
              value={secrets.awsRegion === undefined ? settings.credentials.region : secrets.awsRegion}
              onChange={e => credential('awsRegion', e.target.value)}
            />
            <Field
              label="SES configuration set"
              value={
                secrets.configurationSet === undefined
                  ? settings.credentials.configurationSet
                  : secrets.configurationSet
              }
              onChange={e => credential('configurationSet', e.target.value)}
            />
          </div>
          <Field
            label="SNS topic ARN"
            value={secrets.topicArn === undefined ? settings.credentials.topicArn : secrets.topicArn}
            onChange={e => credential('topicArn', e.target.value)}
          />
          <Field
            label="SNS HTTPS subscription endpoint"
            readOnly
            value={settings.callbacks.email}
            hint="Subscribe this endpoint to that topic. Keep raw message delivery disabled; the CRM verifies signed SNS events."
          />
          <p className="msg-connection-detail">
            {settings.verified.email ? '✓ Email connection checked' : 'Email connection not verified'}
          </p>
        </div>
      </div>
      <div className="msg-panel">
        <h3>Store senders</h3>
        <p className="msg-muted">
          Text replies are routed by store number. Email replies go to the Reply-to inbox below.
        </p>
        <Field
          label="Public CRM origin"
          value={settings.publicOrigin}
          onChange={e => change({ publicOrigin: e.target.value })}
        />
        {meta.stores.map(store => {
          const row = settings.stores.find(s => s.store === store._id) || {};
          return (
            <fieldset key={store._id} className="msg-store-settings">
              <legend>{store.name || store.location}</legend>
              <div className="msg-form-pair">
                <Field
                  label="Business texting number"
                  value={row.smsFrom || ''}
                  onChange={e => storeChange(store._id, 'smsFrom', e.target.value)}
                  placeholder="+12105551234"
                />
                <Field label="Store time zone">
                  <select
                    value={row.timezone || 'America/Chicago'}
                    onChange={e => storeChange(store._id, 'timezone', e.target.value)}
                  >
                    <option value="America/Chicago">Central</option>
                    <option value="America/New_York">Eastern</option>
                    <option value="America/Denver">Mountain</option>
                    <option value="America/Phoenix">Arizona</option>
                    <option value="America/Los_Angeles">Pacific</option>
                  </select>
                </Field>
              </div>
              <div className="msg-form-pair">
                <Field
                  label="Email From address"
                  type="email"
                  value={row.fromEmail || ''}
                  onChange={e => storeChange(store._id, 'fromEmail', e.target.value)}
                />
                <Field
                  label="Email Reply-to address"
                  type="email"
                  value={row.replyTo || ''}
                  onChange={e => storeChange(store._id, 'replyTo', e.target.value)}
                />
              </div>
              <Field
                label="Business mailing address"
                value={row.postalAddress || ''}
                onChange={e => storeChange(store._id, 'postalAddress', e.target.value)}
                placeholder="Street address, city, state, ZIP"
              />
            </fieldset>
          );
        })}
      </div>
      <div className="msg-panel">
        <h3>Delivery controls</h3>
        <p>
          {settings.enabled
            ? 'Delivery is enabled. Eligible queued messages can be sent.'
            : 'Delivery is paused. Drafts, audience previews, and the inbox remain available.'}
        </p>
        {settings.verified.notes?.length > 0 && (
          <ul>
            {settings.verified.notes.map(note => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        )}
        {settings.verified.checkedAt && <small>Last provider check: {dateLabel(settings.verified.checkedAt)}</small>}
        {!settings.enabled && (
          <label className="msg-checkbox">
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
            MHC’s sending accounts, business numbers, and sender registrations are ready. Enabling delivery resumes
            eligible queued messages.
          </label>
        )}
        <div className="msg-editor-actions">
          <button
            className="msg-button secondary"
            disabled={busy}
            onClick={() =>
              work(async () => {
                await persist(false);
                notify('Settings saved. Delivery is paused until enabled.');
              })
            }
          >
            Save settings
          </button>
          <button
            className="msg-button secondary"
            disabled={busy || dirty}
            onClick={() =>
              work(async () => {
                const result = await api('/settings/verify', {});
                setSettings(result);
                setAck(false);
                onSaved();
                notify('Provider checks finished. Review the connection results.');
              })
            }
          >
            Check connections
          </button>
          {settings.enabled ? (
            <button
              className="msg-button"
              disabled={busy || dirty}
              onClick={() =>
                work(async () => {
                  await persist(false);
                  notify('Delivery paused. A message already in flight may still arrive.');
                })
              }
            >
              Pause all sending
            </button>
          ) : (
            <button
              className="msg-button"
              disabled={busy || dirty || !ack || !(settings.verified.sms || settings.verified.email)}
              onClick={() =>
                work(async () => {
                  await persist(true);
                  notify('Delivery enabled.');
                })
              }
            >
              Enable delivery
            </button>
          )}
        </div>
        <p className="msg-muted">
          Credentials are encrypted on the server and are never returned to the browser. Sending runs in small batches;
          uncertain deliveries are held for review.
        </p>
      </div>
    </section>
  );
}
